import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from './components/AdminSidebar';
import AdminHeader from './components/AdminHeader';
import DashboardStats from './components/DashboardStats';
import MatchManagement from './components/MatchManagement';
import AdminManagement from './components/AdminManagement';
import ChangePassword from './components/ChangePassword';
import FlagManagement from './components/FlagManagement';
import PlayerManagement from './components/PlayerManagement';
import QuizManagement from './components/QuizManagement';
import QuizResults from './components/QuizResults';
import AdminLeaderboard from './components/AdminLeaderboard';
import ConveyorItemManagement from './components/ConveyorItemManagement';
import ReviewPlayerManagement from './components/ReviewPlayerManagement';
import CategoryManagement from './components/CategoryManagement';
import QuizPoolSettings from './components/QuizPoolSettings';
import SuggestionManagement from './components/SuggestionManagement';
import MatchApiSettings from './components/MatchApiSettings';
import DilemmaManagement from './components/DilemmaManagement';
import AdstraSettings from './components/AdstraSettings';
import AdSenseSettings from './components/AdSenseSettings';
import CountryBattleTeamManagement from './components/CountryBattleTeamManagement';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('adminActiveTab');
    return savedTab || 'dashboard';
  });
  
  const [adminData, setAdminData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    const savedSidebarState = localStorage.getItem('adminSidebarOpen');
    return savedSidebarState ? JSON.parse(savedSidebarState) : false;
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  
  const matchManagementRef = useRef();
  const flagManagementRef = useRef();
  const adminManagementRef = useRef();
  const playerManagementRef = useRef();
  const reviewPlayerManagementRef = useRef();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    const admin = localStorage.getItem('adminData');
    
    if (!token || !admin) {
      localStorage.removeItem('adminActiveTab');
      localStorage.removeItem('adminSidebarOpen');
      navigate('/admin/login');
      return;
    }
    
    setAdminData(JSON.parse(admin));
  }, [navigate]);

  useEffect(() => {
    if (location.state) {
      const { activeTab: newTab, openForm } = location.state;
      
      if (newTab) {
        setActiveTab(newTab);
        localStorage.setItem('adminActiveTab', newTab);
      }
      
      if (openForm) {
        setTimeout(() => {
          if (newTab === 'matches' && matchManagementRef.current?.openAddForm) {
            matchManagementRef.current.openAddForm();
          } else if (newTab === 'flags' && flagManagementRef.current?.openAddForm) {
            flagManagementRef.current.openAddForm();
          } else if (newTab === 'admins' && adminManagementRef.current?.openAddForm) {
            adminManagementRef.current.openAddForm();
          } else if (newTab === 'players' && playerManagementRef.current?.openAddForm) {
            playerManagementRef.current.openAddForm();
          } else if (newTab === 'review-players' && reviewPlayerManagementRef.current?.openAddForm) {
            reviewPlayerManagementRef.current.openAddForm();
          }
        }, 100);
      }
      
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Listen for custom tab change events from child components
  useEffect(() => {
    const handleTabChangeEvent = (event) => {
      if (event.detail) {
        handleTabChange(event.detail);
      }
    };
    window.addEventListener('changeAdminTab', handleTabChangeEvent);
    return () => window.removeEventListener('changeAdminTab', handleTabChangeEvent);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('adminActiveTab', tab);
  };

  const handleSidebarToggle = (isOpen) => {
    setSidebarOpen(isOpen);
    localStorage.setItem('adminSidebarOpen', JSON.stringify(isOpen));
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    localStorage.removeItem('adminActiveTab');
    localStorage.removeItem('adminSidebarOpen');
    navigate('/admin/login');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardStats />;
      
      case 'matches':
        return <MatchManagement ref={matchManagementRef} />;
      
      case 'match-api-settings':
        return <MatchApiSettings />;
      
      case 'conveyor-items':
        return <ConveyorItemManagement />;
      
      case 'flags':
        return <FlagManagement ref={flagManagementRef} />;
      
      case 'players':
        return <PlayerManagement ref={playerManagementRef} />;
      
      case 'review-players':
        return <ReviewPlayerManagement ref={reviewPlayerManagementRef} />;
      
      case 'categories':
        return <CategoryManagement />;
      
      case 'quiz':
        return <QuizManagement />;
      
      case 'pool-settings':
        return <QuizPoolSettings />;
      
      case 'quiz-results':
        return <QuizResults />;
      
      case 'leaderboard':
        return <AdminLeaderboard />;
      
      case 'suggestions': // ADDED: Suggestions case
        return <SuggestionManagement />;
      
      case 'fun-dilemmas':
        return <DilemmaManagement />;
      
      case 'country-battle-teams':
        return <CountryBattleTeamManagement />;
      
      case 'adstra-settings':
        return <AdstraSettings />;
      
      case 'adsense-settings':
        return <AdSenseSettings />;
      
      case 'admins':
        return <AdminManagement ref={adminManagementRef} />;
      
      case 'password':
        return <ChangePassword />;
      
      default:
        return <DashboardStats />;
    }
  };

  if (!adminData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      <AdminSidebar 
        activeTab={activeTab} 
        setActiveTab={handleTabChange}
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={handleSidebarToggle}
        adminData={adminData}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <AdminHeader 
          adminData={adminData} 
          handleLogout={handleLogout} 
          setSidebarOpen={handleSidebarToggle}
        />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-full mx-auto">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
